import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export interface TableParamsOptions {
  defaultLimit?: number;
  defaultParams?: Record<string, string | number | undefined | null>;
}

export function useTableParams(options: TableParamsOptions = {}) {
  const { defaultLimit = 10, defaultParams = {} } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const setSearchParamsRef = useRef(setSearchParams);
  const searchParamsRef = useRef(searchParams);

  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const search =
    searchParams.get("search") ??
    (defaultParams.search !== undefined ? String(defaultParams.search) : "");

  const page =
    Number(searchParams.get("page") ?? defaultParams.page ?? "1") || 1;

  const limit =
    Number(
      searchParams.get("limit") ?? defaultParams.limit ?? String(defaultLimit)
    ) || defaultLimit;

  const sortKey = searchParams.get("sortBy") ?? undefined;
  const sortOrder =
    (searchParams.get("sortOrder") as "asc" | "desc") ?? undefined;

  const updateParams = useCallback(
    (
      newParams: Record<
        string,
        string | number | boolean | (string | number)[] | undefined | null
      >,
      navOptions: { replace?: boolean } = { replace: true }
    ) => {
      setSearchParamsRef.current(
        (prev) => {
          const params = new URLSearchParams(prev.toString());
          for (const [key, value] of Object.entries(newParams)) {
            if (
              value === "" ||
              value === null ||
              value === undefined ||
              (Array.isArray(value) && value.length === 0)
            ) {
              params.delete(key);
            } else if (Array.isArray(value)) {
              params.set(key, value.join(","));
            } else {
              params.set(key, String(value));
            }
          }
          return params;
        },
        navOptions
      );
    },
    []
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      const currentSearch = searchParamsRef.current.get("search") ?? "";
      if (value === currentSearch) return;
      updateParams({ search: value, page: 1 });
    },
    [updateParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ page: newPage });
    },
    [updateParams]
  );

  const handleSort = useCallback(
    (key: string | undefined, order: "asc" | "desc" | undefined) => {
      updateParams({ sortBy: key, sortOrder: order, page: 1 });
    },
    [updateParams]
  );

  const getParam = useCallback(
    (key: string, defaultValue: string = "") => {
      return searchParams.get(key) ?? defaultValue;
    },
    [searchParams]
  );

  const getArrayParam = useCallback(
    (key: string): string[] => {
      const val = searchParams.get(key);
      if (!val) return [];
      return val.split(",").filter(Boolean);
    },
    [searchParams]
  );

  const resetParams = useCallback(() => {
    setSearchParamsRef.current(new URLSearchParams(), { replace: true });
  }, []);

  return {
    searchParams,
    search,
    page,
    limit,
    sortKey,
    sortOrder,
    updateParams,
    handleSearchChange,
    handlePageChange,
    handleSort,
    getParam,
    getArrayParam,
    resetParams,
  };
}
